import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "react-bootstrap";
import FeedBackModal from "../../components/modals/FeedBackModal";
import YuJaButton from "../../components/standardization/YuJaButton";
import { Spin } from "../../components/standardization/YuJaLoading";
import { ReactComponent as ModalClose } from "../../images/modal_close.svg";
import { useLoading } from "../../utils/LoadingContext";
import {
    BUTTON,
    HOME_PATH,
    LOGIN_PATH,
    SCREEN_TYPE,
    TEXTBOX,
    UNLIMITED_ATTEMPTS
} from "../../utils/constants";
import { newPageLog, removePrefix } from "../../utils/helpers";
import ClassResultAllMerged from "./ClassResultAllMerged";
import thankYou from "./animations/thankYou.json";
import "./styles.css";
import styles from "./styles.module.css";
import PollTitleContainer from "./PollTitleContainer";
import Lottie from "react-lottie";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {useHistory} from "react-router-dom";
import {useSession} from "../../hooks/useSession";
import PollReadyClasses from "./PollReady.module.css";
import {useCountdown} from "../../hooks/useCountdown";

export default function UserCompleteMergedPoll({
    stopped = false,
    websocketState,
    handleReattempt,
    allowReattempt,
    attemptsCount,
    maxAttempts,
    pollTitle,
    currentPoll,
    institutionId,
    userName,
    pollInfo, 
    pollEndTime,
    handlePollComplete,
    setCountdownContent
}) {
    const { t } = useTranslation();

    const {type} = useWindowDimensions();

    const history = useHistory();
    const {isLoggedIn, isFromGradeLink} = useSession();
    const { loading } = useLoading();
    const { getReturnValues, resetCountdown } = useCountdown(pollEndTime, false);
    const [days, hours, minutes, seconds, ] = getReturnValues;



    const [showClassResult, setShowClassResult] = useState(false);
    const [canReattempt, setCanReattempt] = useState(false);
    const [feedbackModalShow, setFeedBackModalShow] = useState(false);
    const [showReattemptModal, setShowReattemptModal] = useState(false);

    const LottieOptions = {
        loop: true,
        autoplay: true,
        animationData: thankYou,
        rendererSettings: {
            preserveAspectRatio: "xMidYMid slice",
        }
    };

    useEffect(() => {
        newPageLog("UserCompletePoll.js");
    }, []);

    useEffect(() => {
        resetCountdown(pollEndTime);
    }, [pollEndTime]);

    useEffect(() => {
        if (!stopped && days + hours + minutes + seconds <= 0) {
            handlePollComplete();
        }
    }, [stopped, days, hours, minutes, seconds]);

    useEffect(() => {
        const now = Date.now();
        if (now > pollEndTime) {
            handlePollComplete();
        }
    }, [handlePollComplete, pollEndTime]);


    useEffect(() => {
        setCanReattempt(allowReattempt && (maxAttempts === UNLIMITED_ATTEMPTS || attemptsCount < maxAttempts));
    }, [allowReattempt, attemptsCount, maxAttempts]);

    const onClickReattempt = () => {
        setShowReattemptModal(true);
    };

    const handleClose = () => {
        setShowReattemptModal(false);
    };

    useEffect(() => {
        if (stopped) {
            setShowReattemptModal(false);
        }
    }, [stopped]);

    if (showClassResult) {
        return (
            <ClassResultAllMerged
                currentPoll={currentPoll}
                institutionId={institutionId}
                websocketState={websocketState}
                pollInfo={pollInfo}
                pollTitle={pollTitle}
                userName={userName}
                setCountdownContent={setCountdownContent}
                setShowClassResult={setShowClassResult}
            />
        );
    }


    return (
        <div className={PollReadyClasses.parentContainer} style={{gap: 10, overflow: "auto"}}>
            <FeedBackModal
                show={feedbackModalShow}
                setModalShow={setFeedBackModalShow}
                pollKey={removePrefix(currentPoll.questions[0].pollKey)}
                pollUniqueCode={currentPoll.questions[0].pollSortKey.split("#")[2]}
                userId={userName}
                institutionId={institutionId}
            />
            <Modal
                id="reattempt-modal"
                show={showReattemptModal}
                onHide={handleClose}
                aria-labelledby="contained-modal-title-vcenter"
                backdrop={loading ? "static" : true}
                dialogClassName="sa-modal"
            >
                <Spin tip="Loading..." size="large" spinning={loading}>
                    <ModalClose tabIndex={0} role={BUTTON} aria-label={t('accessibility.closeModal')} onClick={handleClose}
                                className="modal-close"/>
                    <Modal.Header style={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                        <Modal.Title tabIndex={0} role={TEXTBOX}>
                            {t('poll.reattemptModalTitle')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body tabIndex={0} role={TEXTBOX}>
                        {t('poll.reattemptModalBody')}
                    </Modal.Body>
                    <Modal.Footer>
                        <YuJaButton type="secondary" onClick={handleClose}>
                            {t('common.no')}
                        </YuJaButton>
                        <YuJaButton onClick={handleReattempt}>
                            {t('common.yes')}
                        </YuJaButton>
                    </Modal.Footer>
                </Spin>
            </Modal>

            <div className={PollReadyClasses.progressBarContainer} style={{marginBottom: 15}}>
                <span className={PollReadyClasses.progressBar} style={{width: "100%"}}/>
            </div>
            <PollTitleContainer
                pollTitle={pollTitle}
                style={{width: "calc(100% - 20px)", alignSelf: "center"}}
            />

            <div style={{flex: 1}}/>

            <Lottie
                options={LottieOptions}
                width={type === SCREEN_TYPE.MOBILE ? 400 : 600}
                style={{overflow: 'unset', height: "unset", cursor: "default"}}
                isClickToPauseDisabled={true}
            />

            <span className={styles.emptyBoxTitle}>
                {stopped ? t('poll.endedPollText') : t('poll.youAreAllDoneText')}
            </span>
            <span style={{height: 20}}/>
            <span className={styles.emptyBoxDesc} style={{width: "calc(100% - 80px)", alignSelf: "center"}}>
                {stopped && t('poll.userCompleteMergedResponses')}
                {!stopped && canReattempt && t('poll.userCompletePollReattemptSubtitle')}
                {!stopped && !canReattempt && t('poll.userCompletePollSubtitle')}
            </span>

            <div style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                gap: 10,
                marginTop: 30
            }}>
                {!isFromGradeLink() &&
                    <YuJaButton
                        onClick={() => history.push({pathname: isLoggedIn() ? HOME_PATH : LOGIN_PATH})}
                        style={{
                            fontSize: 14, 
                            lineHeight: 1.6,
                            width: 130,
                            height: 35,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        {t('poll.closeLabel')}
                    </YuJaButton>
                }


                {stopped &&
                    <YuJaButton
                        style={{
                            fontSize: 14, 
                            lineHeight: 1.6,
                            width: 130,
                            height: 35,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            whiteSpace: "nowrap"
                        }}
                        onClick={() => setShowClassResult(true)}
                    >
                        {t('poll.seeResponsesButtonText')}
                    </YuJaButton>
                }
                {canReattempt && !stopped &&
                    <YuJaButton
                        onClick={onClickReattempt}
                        style={{
                            fontSize: 14, 
                            lineHeight: 1.6,
                            width: 130,
                            height: 35,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        {t('poll.retryLabel')}
                    </YuJaButton>
                }
            </div>


            <div style={{flex: 2}}/>

            <YuJaButton
                style={{
                    position: "fixed", right: 0,
                    transform: "translate(50%, 50%) rotate(-90deg)",
                    transformOrigin: "bottom",
                    top: "calc(80% - 90px)"
                }}
                type={"secondary"}
                onClick={() => setFeedBackModalShow(true)}
            >
                {t('poll.feedbackButtonText')}
            </YuJaButton>
        </div>
    );
}